import React, { memo, useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { scaleLinear } from 'd3-scale';
import { interpolateHcl } from 'd3-interpolate';
import { select } from 'd3-selection';
import { arc } from 'd3-shape';
import { rgb, color } from 'd3-color';
import { Box } from 'investira.react.components';

const DonutChart = memo(props => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const svgRef = useRef();

  function radius(pWidth, pHeight, pMargin) {
    return Math.min(pWidth, pHeight) / 1.6 - pMargin;
  }

  const palette = useCallback(
    (pDomain, pRangeColors = props.rangeColors) => {
      const xPalette = [];
      let xRange = pRangeColors;

      if (pRangeColors.length === 1) xRange = [xRange[0], rgb(xRange[0]).darker()];
      if (pDomain.length === 1) xRange = [xRange[0], xRange[0]];

      const xStep = scaleLinear()
        .domain([0, xRange.length - 1])
        .range([0, pDomain.length - 1]);

      const xSteppedDomain = pDomain.map((_, xIndex) =>
        xIndex === 0 || xIndex === pDomain.length - 1 ? xIndex : xStep(xIndex)
      );

      const xDomain = pDomain.length === 1 ? [0, pDomain.length - 1] : xSteppedDomain;

      const xColor = scaleLinear().domain(xDomain).range(xRange).interpolate(interpolateHcl);

      for (let i = 0; i < pDomain.length; i++) {
        xPalette.push(color(xColor(i)).formatHex());
      }

      return xPalette;
    },
    [props.rangeColors]
  );

  // const mountChart = useCallback(
  //   (container, pSerie) => {
  //     if (!container || !pSerie || Object.keys(pSerie).length === 0) return;

  //     const { width, height } = container.getBoundingClientRect();
  //     const xRadius = radius(width, height, 0);

  //     const svg = select(container)
  //       .selectAll('svg')
  //       .data([null])
  //       .join('svg')
  //       .attr('width', width)
  //       .attr('height', height);

  //     const g = svg
  //       .selectAll('g')
  //       .data([null])
  //       .join('g')
  //       .attr('transform', `translate(${width / 2}, ${height / 2})`);

  //     const hasCustomAngles = props.startAngle !== undefined && props.endAngle !== undefined;

  //     const total = Object.values(pSerie).reduce((a, b) => a + b, 0);
  //     if (total === 0) return;

  //     let cumulativeAngle = hasCustomAngles ? props.startAngle : 0;

  //     const angleRange = hasCustomAngles ? props.endAngle - props.startAngle : 2 * Math.PI;

  //     const data = Object.entries(pSerie).map(([key, value]) => {
  //       const sliceAngle = angleRange * (value / total);

  //       const slice = {
  //         key,
  //         value,
  //         startAngle: cumulativeAngle,
  //         endAngle: cumulativeAngle + sliceAngle
  //       };

  //       cumulativeAngle += sliceAngle;
  //       return slice;
  //     });

  //     const colors = palette(Object.keys(pSerie));

  //     const arcGenerator = arc()
  //       .innerRadius(xRadius * 0.7)
  //       .outerRadius(xRadius * 0.8);

  //     g.selectAll('path')
  //       .data(data, d => d.key)
  //       .join('path')
  //       .attr('d', arcGenerator)
  //       .attr('fill', (_, i) => colors[i])
  //       .style('opacity', (_, i) => (i === props.index ? 1 : 0.2));
  //   },
  //   [palette, props.index, props.startAngle, props.endAngle]
  // );

  const mountChart = useCallback(
    (container, pSerie) => {
      if (!container || !pSerie || Object.keys(pSerie).length === 0) return;

      const { width, height } = container.getBoundingClientRect();
      const xRadius = radius(width, height, 0);

      const svg = select(container)
        .selectAll('svg')
        .data([null])
        .join('svg')
        .attr('width', width)
        .attr('height', height);

      const g = svg
        .selectAll('g')
        .data([null])
        .join('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`);

      const total = Object.values(pSerie).reduce((a, b) => a + b, 0);
      if (total === 0) return;

      const hasCustomAngles = props.startAngle !== undefined && props.endAngle !== undefined;

      let data = [];

      if (hasCustomAngles) {
        const start = props.startAngle;
        const end = props.endAngle;

        data = [
          {
            key: 'active',
            startAngle: start,
            endAngle: end
          },
          {
            key: 'rest',
            startAngle: end,
            endAngle: start + 2 * Math.PI
          }
        ];
      } else {
        let cumulativeAngle = 0;

        data = Object.entries(pSerie).map(([key, value]) => {
          const sliceAngle = 2 * Math.PI * (value / total);

          const slice = {
            key,
            startAngle: cumulativeAngle,
            endAngle: cumulativeAngle + sliceAngle
          };

          cumulativeAngle += sliceAngle;
          return slice;
        });
      }

      const colors = palette(Object.keys(pSerie));

      const arcGenerator = arc()
        .innerRadius(xRadius * 0.7)
        .outerRadius(xRadius * 0.8);

      g.selectAll('path')
        .data(data, d => d.key)
        .join('path')
        .attr('d', arcGenerator)
        .attr('fill', (d, i) => (hasCustomAngles ? colors[0] : colors[i]))
        .style('opacity', (_, i) => (i === props.index ? 1 : 0.2));
    },
    [palette, props.startAngle, props.endAngle]
  );

  useEffect(() => {
    if (svgRef.current) {
      const xSvgRect = svgRef.current.getBoundingClientRect();
      setWidth(xSvgRect.width);
      setHeight(xSvgRect.height);
    }
  }, []);

  useEffect(() => {
    if (!props.serie || !svgRef.current) return;
    mountChart(svgRef.current, props.serie);
  }, [width, height, props.rangeColors, props.serie, mountChart]);

  return (
    <Box
      component="figure"
      ref={svgRef}
      id={`donut-${props.id}`}
      width="80px"
      height="80px"
      margin="0"
      position="relative"
      sx={props.sx}>
      <Box
        component="figcaption"
        sx={{
          color: `var(--color-${props.color})`,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: `${Math.min(width, height) * 0.18}px`
        }}>
        {`${props.percentual || 0}%`}
      </Box>
    </Box>
  );
});

DonutChart.propTypes = {
  label: PropTypes.string,
  percentual: PropTypes.number,
  rangeColors: PropTypes.array,
  color: PropTypes.string,
  serie: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  startAngle: PropTypes.number,
  endAngle: PropTypes.number,
  index: PropTypes.number
};

DonutChart.defaultProps = {
  rangeColors: ['#00dfa8', '#64eeff', '#f1b40f', '#ff7957'],
  color: 'white-90',
  serie: {}
};

export default DonutChart;
